import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { CaseStudyShell } from '../components/case-study/CaseStudyShell';
import { CaseHeader } from '../components/case-study/CaseHeader';
import { CaseSection } from '../components/case-study/CaseSection';
import { ImageFigure } from '../components/case-study/ImageFigure';
import { NextCase } from '../components/case-study/NextCase';
import { Reveal } from '../components/Reveal';
import { DecisionGrid } from '../components/botkeeper/DecisionGrid';
import { AttentionStudy } from '../components/botkeeper/AttentionStudy';
import { botkeeperImages, botkeeperMeta, botkeeperMethods, botkeeperPainPoints, botkeeperResults, botkeeperShipping, botkeeperTools } from '../data/botkeeper';
export function BotkeeperCaseStudy() {
  return <CaseStudyShell data-id="element-710" data-mp="2f2rcpwg6l26v">
      <CaseHeader overline="Case study · AI accounting platform" title="Redesigning permission management for a firm that manages firms" intro="Botkeeper operates on a three-tier hierarchy — the platform, accounting firms, and their clients. When nobody agreed on where user management lived, everything broke. This is how we fixed it." meta={botkeeperMeta} data-id="element-711" data-mp="2f295k4rzd3k6" />
      <ul aria-label="Tools" className="mt-5 flex flex-wrap gap-2" data-id="element-712" data-mp="22ahkmp1v5jne">
        {botkeeperTools.map(t => <li key={t} className="rounded-full bg-ink-soft px-3 py-1 text-xs font-medium text-ink" data-id="element-713" data-mp="277r6juq1nd6h">
            {t}
          </li>)}
      </ul>

      <ImageFigure className="mt-14" {...botkeeperImages.hero} caption="Client & User Management, before and after." data-id="element-714" data-mp="1ejc3m6k6slbi" />

      {/* 01 — Problem */}
      <CaseSection index="01" label="The problem" title="A three-tier hierarchy with zero visual clarity" data-id="element-715" data-mp="1jh3wp3wkidha">
        <div className="grid gap-10 md:grid-cols-[1.1fr_1fr] md:gap-12" data-id="element-716" data-mp="1oapz8390p0zd">
          <div className="space-y-4 text-[15px] leading-relaxed text-fg-muted" data-id="element-717" data-mp="qszqmha33ye2">
            <p data-id="element-718" data-mp="vskqqv0zqcsd">
              Botkeeper sits between accounting firms and their clients, so every action carries implicit context:{' '}
              <strong className="font-semibold text-fg" data-id="element-719" data-mp="nqv7uagr7w87">
                who is the firm, who is their client, and at which level is this user working?
              </strong>
            </p>
            <p data-id="element-720" data-mp="xgwm6ajb209x">
              The old interface treated all of this as a flat list. Details, Users and Service Team tabs sat side by side
              with no hierarchy, so users had to build the relationships in their heads.
            </p>
            <blockquote className="border-l-2 border-coral py-1 pl-5 text-base font-medium leading-relaxed text-fg" data-id="element-721" data-mp="ryoajg9dm8j">
              “Each team pointed users to a different starting page. Nobody agreed on where user management lived — and
              that confusion flowed downstream to clients.”
            </blockquote>
            <p data-id="element-722" data-mp="1q6dj9lhve5ch">
              Support tickets confirmed the damage:{' '}
              <strong className="font-semibold text-fg" data-id="element-723" data-mp="1q8pxbnkibvk9">72% of issues traced back to a single hidden Edit action.</strong>{' '}
              Power users had built workarounds; in onboarding, new users gave up entirely.
            </p>
          </div>
          <ul className="divide-y divide-line rounded-2xl border border-line bg-raised px-5 md:px-6" data-id="element-724" data-mp="385l2l3nodq3e">
            {botkeeperPainPoints.map((p, i) => <li key={p.title} className="grid grid-cols-[24px_1fr] gap-3 py-5" data-id="element-725" data-mp="2z3twvyt4xj9j" data-mpi={i}>
                <span className="grid h-6 w-6 place-items-center rounded-full bg-coral-soft text-[11px] font-semibold tabular-nums text-rust" data-id="element-726" data-mp="2z5y00ej7me8r">
                  {i + 1}
                </span>
                <div data-id="element-727" data-mp="3qx41ayao2hzm">
                  <h3 className="text-sm font-semibold text-fg" data-id="element-728" data-mp="3d3mgzba5p18j">{p.title}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-fg-muted" data-id="element-729" data-mp="3d1if0yvticaw">{p.body}</p>
                </div>
              </li>)}
          </ul>
        </div>
        <ImageFigure className="mt-10" {...botkeeperImages.oldDetails} caption="Before: every panel read-only, with a small Edit link as the only way in." data-id="element-730" data-mp="9m4bhahuw6ba" />
      </CaseSection>

      {/* 02 — Research */}
      <CaseSection index="02" label="Research & discovery" title="Four methods. One consistent signal." data-id="element-731" data-mp="2lutdwmqulh2d">
        <dl className="grid gap-px overflow-hidden rounded-2xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-4" data-id="element-732" data-mp="2cwhpmrdgevd3">
          {botkeeperMethods.map((m, i) => <Reveal key={m.title} delay={i * 0.04} className="bg-raised p-5 md:p-6" data-id="element-733" data-mp="27z92ecdg814z" data-mpi={i}>
              <dt data-id="element-734" data-mp="34n5n9tfx5im0">
                <span className="block text-3xl font-semibold tracking-tight text-ink" data-id="element-735" data-mp="34prtnpr2ps2n">{m.stat}</span>
                <span className="mt-2 block text-sm font-semibold text-fg" data-id="element-736" data-mp="30o6l2glknzy9">{m.title}</span>
              </dt>
              <dd className="mt-2 text-sm leading-relaxed text-fg-muted" data-id="element-737" data-mp="2vt0rqwpubj05">{m.body}</dd>
            </Reveal>)}
        </dl>
        <ImageFigure className="mt-10" {...botkeeperImages.notes} caption="Research synthesis: feature mapping and user flows across the hierarchy levels." data-id="element-738" data-mp="3oc6drm2g20fh" />
        <Reveal data-id="element-739" data-mp="3qwm5rbsjlnz3">
          <figure className="mt-10 rounded-3xl bg-ink-deep px-6 py-10 md:px-12 md:py-14" data-id="element-740" data-mp="3qx4c518x5ikq">
            <blockquote className="max-w-3xl text-xl font-semibold leading-snug text-white md:text-[26px]" data-id="element-741" data-mp="3vudyg810he4c">
              The core problem wasn’t the Edit button. It was that the interface never told users where they were in the
              hierarchy — so every action felt uncertain.
            </blockquote>
            <figcaption className="mt-5 text-xs text-white/60" data-id="element-742" data-mp="doj6c0vikcp9">Synthesis across all four research methods</figcaption>
          </figure>
        </Reveal>
      </CaseSection>

      {/* 03 — Decisions */}
      <CaseSection index="03" label="Design decisions" title="Four bets, each validated before shipping" data-id="element-743" data-mp="ngf5zgd90avl">
        <DecisionGrid data-id="element-744" data-mp="sd5k3y5hswed" />
        <AttentionStudy data-id="element-745" data-mp="sgjk4zqootin" />
      </CaseSection>

      {/* Shipping context */}
      <Reveal data-id="element-746" data-mp="whn0dncn8rke">
        <section aria-labelledby="bk-shipping" className="mt-20 grid gap-8 rounded-3xl bg-night px-6 py-10 text-white md:mt-28 md:grid-cols-2 md:gap-12 md:px-12 md:py-14" data-id="element-747" data-mp="whvcve4utuv2">
          <div data-id="element-748" data-mp="2xg725kyjylm3">
            <h2 id="bk-shipping" className="text-2xl font-semibold leading-snug tracking-tight md:text-[27px]" data-id="element-749" data-mp="2xfxdq1t5omii">
              Shipping in the middle of a platform redesign
            </h2>
            <p className="mt-4 text-[15px] leading-relaxed text-white/75" data-id="element-750" data-mp="37a5y2281995l">
              This project launched halfway through a full platform redesign with a phased rollout, so the new navigation
              had to coexist with old screens. That meant constant alignment with engineering on what “done” meant for
              each phase.
            </p>
          </div>
          <ul className="space-y-4" data-id="element-751" data-mp="3q6fc4pixpzpl">
            {botkeeperShipping.map(s => <li key={s} className="flex gap-3 text-sm leading-relaxed text-white/85" data-id="element-752" data-mp="3l8xdbrymt0vb">
                <ArrowRightIcon className="mt-0.5 h-4 w-4 shrink-0 text-coral" aria-hidden data-id="element-753" data-mp="1vs4oulhzndlx" />
                {s}
              </li>)}
          </ul>
        </section>
      </Reveal>

      {/* 04 — Before & after */}
      <CaseSection index="04" label="Before & after" title="From fragmented tabs to a clear hierarchy" data-id="element-754" data-mp="6cc5ps0geiod">
        <ImageFigure {...botkeeperImages.newUsers} caption="After: client details edited in place, with users in one table, quick actions on each row and bulk tools above." data-id="element-755" data-mp="badjbxf41psg" />
      </CaseSection>

      {/* 05 — Results */}
      <Reveal data-id="element-756" data-mp="3jq5pupftgfj8">
        <section aria-labelledby="bk-results" className="mt-20 rounded-3xl bg-night px-6 py-10 text-white md:mt-28 md:px-12 md:py-14" data-id="element-757" data-mp="3jqfeo9p4kgnc">
          <p className="text-xs font-medium text-coral" data-id="element-758" data-mp="3or38120tt9kq">
            <span className="tabular-nums" data-id="element-759" data-mp="3aqsetsc1niwe">05</span> / Results
          </p>
          <h2 id="bk-results" className="mt-2 text-2xl font-semibold leading-snug tracking-tight md:text-[27px]" data-id="element-760" data-mp="3erlyuamd8dk0">
            Measured impact within the first month
          </h2>
          <dl className="mt-10 grid gap-y-8 sm:grid-cols-2 md:grid-cols-4 md:divide-x md:divide-white/15" data-id="element-761" data-mp="18wt4b96qx5kr">
            {botkeeperResults.map(r => <div key={r.label} className="md:px-6 md:first:pl-0" data-id="element-762" data-mp="pue8bv1yath">
                <dt className="sr-only" data-id="element-763" data-mp="3rxgoinca1bjb">{r.label}</dt>
                <dd data-id="element-764" data-mp="3rxoxw596xypr">
                  <span className="block text-4xl font-semibold tracking-tight" data-id="element-765" data-mp="3rzjbeyi4uojt">{r.value}</span>
                  <span className="mt-2 block text-sm text-white/80" data-id="element-766" data-mp="3n4dtearupu10">{r.label}</span>
                  <span className="mt-1 block text-xs text-white/50" data-id="element-767" data-mp="3i73tu2ar01zg">{r.detail}</span>
                </dd>
              </div>)}
          </dl>
          <p className="mt-10 border-t border-white/15 pt-6 text-xs leading-relaxed text-white/55" data-id="element-768" data-mp="3965w21qwkqpb">
            Results are partial: measured in the first month after launch, as part of a phased rollout. Methods: usability
            testing (n = 12), CSM review (n = 5) and GA4 event tracking.
          </p>
        </section>
      </Reveal>

      <NextCase title="Palacio de Hierro: luxury shopping and credit in one app" href="/work/palacio-de-hierro" data-id="element-769" data-mp="12fg8z590dzx0" />
    </CaseStudyShell>;
}